import { FC } from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IntervalsModelType, SitesModelType, SitesSourceModelType } from "../../../../../common/modelTypes";
import { col4, col6 } from "../../../../../common/utils";
import ComparisonLinearProgress from "../../../../../components/charts/graph/ComparisonLinearProgress";
import PieChartDistributionType from "../../../../../components/charts/graph/PieChartDistributionType";
import NumericValueWidget from "../../../../../components/charts/NumericValueWidget";
import { MarketBaseType, MarketBaseWidget } from "../../../../../components/common/MarketBaseWidget";
import { EmissionsIntervalResultsDTO } from "../../../../../datasources/api-client/data-contracts";
import { EmissionsTypesColors } from "../../../../../theme/themeValues";
import EmissionsIntervals from "../../../components/ResultsEmissionsIntervals";
import { getDataChart } from "./data";

interface Props {
	emissionsSites: SitesModelType;
	emissionsIntervals: IntervalsModelType;
	emissionsIntervalsPeriodType: "month" | "quarter" | "four-month" | "semester";
	setEmissionsIntervalsPeriodType: (periodType: "month" | "quarter" | "four-month" | "semester") => void;
	emissionsSitesSource: SitesSourceModelType;
	intervalMode: "emissions" | "energy";
	onIntervalModeChange: (mode: "emissions" | "energy") => void;
}

type EmissionKey = keyof EmissionsIntervalResultsDTO;

/**
 * Overview
 * Displays high-level operational units data such as emissions, energy consumption,
 * and emissions distribution. Includes numeric widgets, charts, and key metrics.
 */
const Overview: FC<Props> = ({
	emissionsSites,
	emissionsIntervals,
	emissionsIntervalsPeriodType,
	setEmissionsIntervalsPeriodType,
	emissionsSitesSource,
	intervalMode,
	onIntervalModeChange,
}) => {
	const { t } = useTranslation(["feature.units", "common"]);

	// Memoize dropdown items
	const dropdownItems = [
		{ key: "co2Wtw", label: t("common:emissions") + " tCO2e" },
		{ key: "energyTtw", label: t("common:energy") + " GJ" },
	];

	const selectedKey = intervalMode === "energy" ? "energyTtw" : "co2Wtw";

	const handleSelectKeyChange = (key: string) => {
		onIntervalModeChange(key === "energyTtw" ? "energy" : "emissions");
	};

	const chartData = emissionsIntervals.data?.[selectedKey as EmissionKey]?.data ?? [];
	const chartMeasure = emissionsIntervals.data?.[selectedKey as EmissionKey]?.measure ?? "";

	// Color palette matching data order
	// The order is IMPORTANT
	const colors = [
		EmissionsTypesColors.LightElectricity,
		EmissionsTypesColors.LightFuel,
		EmissionsTypesColors.LightRefrigerant,
		EmissionsTypesColors.DarkProcessGases,
		EmissionsTypesColors.LightHeat,
		EmissionsTypesColors.LightWater,
	];

	// Calculate from and to percentages for the ComparisonLinearProgress component
	const totalEmissions = emissionsSites.data?.total?.current.co2Wtw.value || 0;
	const fromEmissions = emissionsSites.data?.greenRouter.current.co2Wtw.value || 0;
	const toEmissions = emissionsSites.data?.thirdParty.current.co2Wtw.value || 0;

	const fromPercent = totalEmissions !== 0 ? (fromEmissions / totalEmissions) * 100 : 0;
	const toPercent = totalEmissions !== 0 ? (toEmissions / totalEmissions) * 100 : 0;

	const fromEmissionsDatasource = {
		name: "GreenRouter",
		value: fromPercent,
	};

	const toEmissionsDatasource = {
		name: t("common:third parties"),
		value: toPercent,
	};

	const marketBase: MarketBaseType = {
		emissions: emissionsSites.data?.total?.current.marketBasedWtw,
		delta: emissionsSites.data?.total?.current.marketBasedWtwDelta,
		coverage: emissionsSites.data?.total?.current.marketBasedWtwCoverage,
		isLoading: emissionsSites.isLoading,
		onReload: emissionsSites.reload,
	};

	return (
		<Grid container spacing={2}>
			<Grid size={col4} minHeight={150}>
				<NumericValueWidget
					title={t("overview/total emissions")}
					value={emissionsSites.data?.total?.current.co2Wtw.value}
					delta={emissionsSites.data?.total?.delta.co2Wtw}
					measure={emissionsSites.data?.total?.current.co2Wtw.measure}
					isLoading={emissionsSites.isLoading}
					onReload={emissionsSites.reload}
					graph={<ComparisonLinearProgress from={fromEmissionsDatasource} to={toEmissionsDatasource} />}
				/>
			</Grid>

			<Grid size={col4} minHeight={150}>
				<NumericValueWidget
					title={t("overview/consumed energy")}
					value={emissionsSites.data?.total?.current.energyTtw.value}
					delta={emissionsSites.data?.total?.delta.energyTtw}
					measure={emissionsSites.data?.total?.current.energyTtw.measure}
					isLoading={emissionsSites.isLoading}
					onReload={emissionsSites.reload}
					coverage={emissionsSites.data?.total?.current.energyTtwCoverage}
				/>
			</Grid>

			<Grid size={col4} minHeight={150}>
				<NumericValueWidget
					title={t("overview/no operational units")}
					value={emissionsSites.data?.total?.current.count}
					isLoading={emissionsSites.isLoading}
					onReload={emissionsSites.reload}
				/>
			</Grid>

			<Grid size={12} minHeight={500}>
				<EmissionsIntervals
					mode="select"
					selectTitle={t("greenrouter/trend")}
					periodType={emissionsIntervalsPeriodType}
					setPeriodType={setEmissionsIntervalsPeriodType}
					onReload={emissionsIntervals.reload}
					isLoading={emissionsIntervals.isLoading}
					chartData={chartData}
					chartMeasure={chartMeasure}
					menuOptions={dropdownItems}
					selectedKey={selectedKey}
					handleSelectKeyChange={handleSelectKeyChange}
					intervalMode={intervalMode}
					onIntervalModeChange={onIntervalModeChange}
				/>
			</Grid>

			<Grid size={col6} minHeight={150}>
				<NumericValueWidget
					title={t("overview/surface")}
					value={emissionsSites.data?.total?.current.area.value}
					measure={emissionsSites.data?.total?.current.area.measure}
					isLoading={emissionsSites.isLoading}
					onReload={emissionsSites.reload}
					coverage={emissionsSites.data?.total?.current.areaCoverage}
				/>
			</Grid>

			<Grid size={col6} minHeight={150}>
				<NumericValueWidget
					title={t("overview/emission intensity")}
					value={emissionsSites.data?.total?.current.intensityWtw.value}
					measure={emissionsSites.data?.total?.current.intensityWtw.measure}
					isLoading={emissionsSites.isLoading}
					onReload={emissionsSites.reload}
					coverage={emissionsSites.data?.total?.current.areaCoverage} // Same coverage as surface
				/>
			</Grid>

			<Grid size={12} height={350}>
				<PieChartDistributionType
					totalEmissions={emissionsSitesSource.data?.total.value}
					keyEmissions={emissionsSitesSource.data?.total.measure}
					title={t("greenrouter/distribution of emissions by source")}
					isLoading={emissionsSitesSource.isLoading}
					onReload={emissionsSitesSource.reload}
					dataChart={emissionsSitesSource.data && getDataChart(emissionsSitesSource.data, t)}
					colors={colors}
				/>
			</Grid>

			<MarketBaseWidget marketBase={marketBase} />
		</Grid>
	);
};

export default Overview;
